import React from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { linkTabs } from '../tabHref';
import { branchesTabsFor } from './branchesTabsMeta';

/**
 * Branches shell state: which tab the URL selects, which tabs this session may
 * see, and nothing else.
 *
 * As thin as `useRegistersPage`, and for the same reason: neither tab is read
 * over a shell-level window. The branch list is a list of places that exist
 * now, and the shift register carries its own date filter — a range up here
 * would have nothing to scope that the tabs don't already own.
 *
 * The one thing it does decide is **visibility**. This shell is reachable by
 * every signed-in role, and the register inside it is not — see
 * `branchesTabsFor` and its note on why the selected tab survives the filter.
 */
export const useBranchesPage = () => {
    const { pathname } = useLocation();

    // Read once per mount, like `useRegistersPage`: the session cannot change
    // without a navigation that remounts this shell.
    const session = React.useMemo(
        () => ({ role: getUser()?.role ?? null, authMethod: getAuthMethod() }),
        [],
    );

    const { tabs: visibleTabs, selectedTabId } = React.useMemo(
        () => branchesTabsFor(pathname, session.role, session.authMethod),
        [pathname, session],
    );

    // No shell-level range to carry, so the href is the tab's own path — see
    // `linkTabs`. Applied after the role filter, not before: a tab this session
    // may not see should not get a link either.
    const tabs = React.useMemo(() => linkTabs(visibleTabs), [visibleTabs]);

    return {
        tabs,
        selectedTabId,
    };
};
